import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";

export const YE_STEPS = [
  { key: "avstemming", label: "Avstemming", desc: "Sjekker at tall i bank, kunder, leverandører og eiendeler stemmer med virkeligheten." },
  { key: "periodisering", label: "Periodisering", desc: "Fordeler inntekter og kostnader til riktig år." },
  { key: "avskrivninger", label: "Avskrivninger og beregning", desc: "Avskrivninger på utstyr, samt beregning av skatt og årsresultat." },
  { key: "utarbeidelse", label: "Utarbeidelse", desc: "Lager selve regnskapsrapporten (resultatregnskap, balanse og noter)." },
  { key: "innsending", label: "Innsending", desc: "Sender rapporten til offentlige registre." },
  { key: "aksjonaerregister", label: "Aksjonærregisteroppgave", desc: "Innsending av aksjonærregisteroppgaven." },
];

export const defaultYearEndYear = () => new Date().getFullYear() - 1;

export const yearEndYearOptions = () => {
  const y = new Date().getFullYear();
  return [y, y - 1, y - 2, y - 3, y - 4];
};

export const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("nb-NO", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export function YearEndCustomerSection({ customerId }) {
  const [year, setYear] = useState(defaultYearEndYear());
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/customers/${customerId}/year-end?year=${year}`).then((r) => setData(r.data)).catch(() => {});
  }, [customerId, year]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key) => {
    const cur = data?.steps?.[key]?.done;
    const r = await api.put(`/customers/${customerId}/year-end`, { year, step: key, done: !cur });
    setData((p) => ({ ...p, steps: r.data.steps, done_count: r.data.done_count, total: r.data.total }));
  };

  if (data && !data.has_yearend) return null;
  const done = data?.done_count || 0;
  const total = data?.total ?? YE_STEPS.length;
  return (
    <div className="bg-card border border-border rounded-xl p-6 mt-6" data-testid="customer-yearend-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg">Årsregnskap</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{done} av {total} punkter fullført</p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28 bg-secondary border-border" data-testid="customer-yearend-year"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            {yearEndYearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        {YE_STEPS.map((s) => {
          const st = data?.steps?.[s.key] || {};
          const available = st.available !== false;
          if (!available) {
            return (
              <div key={s.key} className="flex items-start gap-3 p-3 rounded-lg border border-dashed border-border bg-secondary/20 opacity-50" data-testid={`customer-yearend-step-${s.key}`}>
                <div className="w-4 h-4 mt-0.5 rounded border border-border shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium flex items-center gap-2">{s.label}
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded px-1.5 py-0.5">Ikke bestilt</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
                </div>
              </div>
            );
          }
          return (
            <div key={s.key} className={`flex items-start gap-3 p-3 rounded-lg border ${st.done ? "bg-emerald-500/5 border-emerald-500/20" : "bg-secondary/40 border-border"}`}>
              <Checkbox checked={!!st.done} onCheckedChange={() => toggle(s.key)} data-testid={`customer-yearend-step-${s.key}`} className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${st.done ? "text-emerald-300" : ""}`}>{s.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
                {st.done && st.by && (
                  <p className="text-xs text-emerald-400/80 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> {st.by} · {fmtWhen(st.at)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
